import React, { useState, useEffect, useRef, useCallback } from 'react';
import useElementHeight from '../hooks/useElementHeight';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { idbSaveMessages, idbGetMessages, idbCleanupOld } from '../utils/idbCache';
import { useArrowKeyFormNav } from '../hooks/useArrowKeyFormNav';
import socket, { joinRoom, leaveRoom } from '../utils/socket';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  PaperPlaneRight, 
  Image as ImageIcon, 
  Camera, 
  Microphone,
  Smiley,
  DotsThreeVertical,
  Copy,
  Star,
  Trash,
  PencilSimple,
  ShareFat,
  ArrowBendUpLeft,
  X,
  Users,
  PushPin,
  PushPinSlash,
  Eye,
  Link,
  DownloadSimple,
  SpeakerSimpleX,
  SpeakerSimpleHigh,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import EmojiPicker from 'emoji-picker-react';
import VoiceMessage from '../components/VoiceMessage';
import PinDurationPicker from '../components/PinDurationPicker';
import WhatsAppInput from '../components/WhatsAppInput';
import MediaPicker from '../components/MediaPicker';
import EditEntryModal from '../components/EditEntryModal';
import WithdrawHistoryModal from '../components/WithdrawHistoryModal';
import SummaryModal from '../components/SummaryModal';
import WithdrawReportModal from '../components/WithdrawReportModal';
import UtrTrailModal from '../components/UtrTrailModal';
import ViewOnceBubble from '../components/ViewOnceBubble';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: Build media URL for group messages
const buildGroupMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API.replace(/\/api\/?$/, '')}${url}`;
  if (url.startsWith('/uploads/')) return `${API.replace(/\/api\/?$/, '')}/api${url}`;
  return `${API}${url}`;
};

// Helper: Get image group starting at index
const getGroupImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url) return null;
  if (msg.text && msg.text.trim()) return null;
  // Check not already part of a previous group
  if (idx > 0 && msgs[idx - 1].image_url && !msgs[idx - 1].voice_url && msgs[idx - 1].sender_id === msg.sender_id && !msgs[idx - 1].text?.trim()) return null;
  const group = [msg];
  for (let i = idx + 1; i < msgs.length; i++) {
    if (group.length >= 10) break;
    const nextMsg = msgs[i];
    if (!nextMsg.image_url || nextMsg.voice_url || nextMsg.sender_id !== msg.sender_id) break;
    if (!nextMsg.text?.trim()) {
      group.push(nextMsg);
    } else {
      // Point 6 — a captioned photo is standalone; stop WITHOUT adding it.
      break;
    }
  }
  return group.length > 1 ? group : null;
};

// Helper: Check if message is part of a group but not the first
const isPartOfGroupImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || idx === 0) return false;
  // Point 6 — a captioned photo is always its own standalone message.
  if (msg.text && msg.text.trim()) return false;
  for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
    const prevMsg = msgs[i];
    if (prevMsg.image_url && !prevMsg.voice_url && prevMsg.sender_id === msg.sender_id && !prevMsg.text?.trim()) {
      return true;
    } else break;
  }
  return false;
};

const GroupChatPage = () => {
  const { groupId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  
  const [group, setGroup] = useState(() => readInstantCache('group_info', groupId));
  const [showWithdrawHistory, setShowWithdrawHistory] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null); // { msg } for coin_cut click confirm
  const [editEntry, setEditEntry] = useState(null); // { type, entryId } for edit modal
  const [messages, setMessages] = useState(() => readInstantCache('group_messages', groupId) || []);
  // Offline-cache indicator — true when the browser reports a live network.
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    idbCleanupOld();
    // Durable IndexedDB paint — instant even offline / after full app close.
    if (groupId) {
      idbGetMessages(groupId).then(idbMsgs => {
        if (idbMsgs && idbMsgs.length) setMessages(prev => (prev && prev.length) ? prev : idbMsgs);
      }).catch(() => {});
    }
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, [groupId]);
  const [loading, setLoading] = useState(false); // Changed to false for instant loading
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  // New states for WhatsApp-like features
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editingGroupMsg, setEditingGroupMsg] = useState(null);
  const [editGroupText, setEditGroupText] = useState('');
  const [showPinDurationPicker, setShowPinDurationPicker] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showVoiceRecordModal, setShowVoiceRecordModal] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // For reply feature
  const [showForwardModal, setShowForwardModal] = useState(false); // For forward feature
  const [availableGroups, setAvailableGroups] = useState([]); // For forward
  const [showMediaPicker, setShowMediaPicker] = useState(false); // For WhatsApp input
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  
  // NEW: Telegram-style features
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedBanner, setShowPinnedBanner] = useState(true);
  const [currentPinnedIndex, setCurrentPinnedIndex] = useState(0);
  const [showAllPinnedMessages, setShowAllPinnedMessages] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [reactionTargetMessage, setReactionTargetMessage] = useState(null);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState({ count: 0, viewers: [] });
  const [showMuteOptions, setShowMuteOptions] = useState(false);

  // Lazy-loading (pagination) — group loads only the latest window; older
  // messages are fetched on scroll-up so no group ever hides newest messages
  // regardless of total size (10k / 20k+).
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  
  // Quick reaction emojis
  const quickReactions = ['👍', '❤️', '🔥', '😂', '😮'];
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // For scroll detection
  const inputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const inputContainerRef = useRef(null); // For keyboard scroll fix
  const prevMessagesLengthRef = useRef(0); // Track previous message count
  const isUserScrollingRef = useRef(false); // Track if user is scrolling
  const isNearBottomRef = useRef(true); // Track if user is near bottom
  const containerRef = useRef(null);
  // Lazy-loading refs — keep stable access to latest state inside callbacks
  // and guard the auto-scroll-to-bottom effect while prepending older msgs.
  const messagesRef = useRef([]);
  const hasMoreRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const isPrependingRef = useRef(false);
  const lastSeenMsgIdRef = useRef(null);
  // Holds the latest loadOlderMessages fn so handleScroll (defined earlier) can
  // call it without a temporal-dead-zone reference to the const below.
  const loadOlderRef = useRef(null);
  // Point-1 — measure live header + pinned-bar heights so the message list
  // offsets by their ACTUAL height (WhatsApp-fixed header, no overlap).
  const chatHeaderRef = useRef(null);
  const headerHeight = useElementHeight(chatHeaderRef, 56);
  const pinnedBarRef = useRef(null);
  const pinnedBarHeight = useElementHeight(pinnedBarRef, 44);
  
  // Point 50 — the keyboard must only open when the user explicitly taps
  // the message box, never automatically on opening a chat. If a search
  // box (or any other input) was focused on the previous screen, the
  // mobile keyboard can otherwise carry over into this page on mount.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && typeof active.blur === 'function') {
      active.blur();
    }
  }, []);

  // Point 51 — Visual Viewport Keyboard Fix for PWA. Pin BOTH height and top
  // of the container to the visual viewport (not just height, like before)
  // so the input bar can't drift up leaving a gap, and auto-scroll to the
  // newest message on keyboard-open so the last bubble/header never ends up
  // hidden behind the keyboard — matching the fix already used in
  // CustomerChat.js / AdminChat.js / HandlerChat.js.
  useEffect(() => {
    const el = containerRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;
    let lastHeight = vv.height;
    const apply = () => {
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
      if (vv.height < lastHeight - 100) {
        requestAnimationFrame(() => {
          try {
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
          } catch {}
        });
      }
      lastHeight = vv.height;
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);
  
  useEffect(() => {
    if (!user || !groupId) {
      navigate('/groups');
      return;
    }

    fetchGroupDetails();
    fetchMessages();
    fetchPinnedMessages();
    fetchMuteStatus();

    // Real-time (Point 5): join this group's socket room and refresh instantly
    // on server-pushed group_message / group_message_updated events. Group
    // chats (incl. the MB Withdraw "G" groups) now update LIVE instead of
    // waiting for the poll — and the poll drops to an 8s safety-net, cutting
    // DB load ~5x per open group chat. Backend already emits these events to
    // room `group_{id}` (coin-cut + mark-as-paid paths).
    const groupRoom = `group_${groupId}`;
    joinRoom(groupRoom, user.id);
    let _rtTimer = null;
    const onGroupEvent = (data) => {
      if (!data || data.group_id !== groupId) return;
      if (_rtTimer) return;              // coalesce bursts into one fetch / 300ms
      _rtTimer = setTimeout(() => { _rtTimer = null; fetchMessages(); }, 300);
    };
    socket.on('group_message', onGroupEvent);
    socket.on('group_message_updated', onGroupEvent);

    // Safety-net poll (was 1500ms — far too aggressive and the main per-open-
    // chat DB load; real-time now handles immediacy).
    pollingIntervalRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      fetchMessages();
      fetchPinnedMessages(); // Also refresh pinned messages on each poll
    }, 15000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (_rtTimer) clearTimeout(_rtTimer);
      socket.off('group_message', onGroupEvent);
      socket.off('group_message_updated', onGroupEvent);
      leaveRoom(groupRoom, user.id);
    };
  }, [groupId, user, navigate]);

  // Fetch pinned messages
  const fetchPinnedMessages = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/pinned-messages`);
      setPinnedMessages(response.data.pinned_messages || []);
    } catch (error) {
      console.error('Failed to fetch pinned messages:', error);
    }
  };

  // Fetch mute status
  const fetchMuteStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/mute-status?user_id=${user.id}`);
      setIsMuted(response.data.is_muted);
    } catch (error) {
      console.error('Failed to fetch mute status:', error);
    }
  };

  // Auto-scroll to bottom - SMART VERSION
  // Only scroll if: initial load, user sent message, OR user is near bottom
  useEffect(() => {
    const currentLength = messages.length;
    const prevLength = prevMessagesLengthRef.current;
    
    // Initial load - always scroll
    if (prevLength === 0 && currentLength > 0) {
      // PW deep-link: if ?scroll_to=<msgId> is present, jump to that message
      // instead of the bottom. Used by the PW badge to open a group at the
      // exact pending withdraw entry (even if it's from a previous day).
      const params = new URLSearchParams(location.search);
      const targetId = params.get('scroll_to');
      if (targetId) {
        setTimeout(() => {
          const el = document.getElementById(`msg-${targetId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'auto', block: 'center' });
            el.classList.add('bg-yellow-100');
            setTimeout(() => el.classList.remove('bg-yellow-100'), 2500);
          } else {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
          }
        }, 120);
      } else {
        // No deep-link: if this group has PENDING coin-cut (withdraw) entries,
        // jump to + highlight the OLDEST pending one instead of scrolling to the
        // bottom — so pending withdrawals are always surfaced when the group is
        // opened directly from the dashboard (matches the PW deep-link UX).
        const firstPending = messages.find(
          (m) => m?.meta?.kind === 'coin_cut_report'
            && m?.meta?.status !== 'paid' && m?.meta?.status !== 'cancelled'
        );
        setTimeout(() => {
          if (firstPending) {
            const el = document.getElementById(`msg-${firstPending.id}`);
            if (el) {
              el.scrollIntoView({ behavior: 'auto', block: 'center' });
              el.classList.add('bg-yellow-100');
              setTimeout(() => el.classList.remove('bg-yellow-100'), 2500);
              return;
            }
          }
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        }, 120);
      }
    }
    // New messages came - only scroll if user is near bottom
    else if (currentLength > prevLength && isNearBottomRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
    }
    // If user is scrolling up, don't auto-scroll
    
    prevMessagesLengthRef.current = currentLength;
  }, [messages]);

  // Keep refs in sync for use inside stable callbacks (pagination).
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  // Detect if user is near bottom of chat + trigger older-message load near top
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Consider "near bottom" if within 150px of bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    isNearBottomRef.current = distanceFromBottom < 150;
    
    // Show/hide scroll to bottom button
    setShowScrollToBottom(distanceFromBottom > 300);

    // Near the top → lazy-load the previous window of older messages.
    if (scrollTop < 120 && hasMoreRef.current && !loadingOlderRef.current) {
      loadOlderRef.current && loadOlderRef.current();
    }
  }, []);

  // Mark messages as read when viewing
  useEffect(() => {
    if (groupId && messages.length > 0 && user?.id) {
      axios.post(`${API}/api/groups/${groupId}/mark-read?user_id=${user.id}`)
        .catch(err => console.error('Mark read failed:', err));
    }
  }, [messages, groupId, user]);

  const fetchGroupDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}`);
      setGroup(response.data);
      writeInstantCache('group_info', groupId, response.data);
    } catch (error) {
      console.error('Failed to fetch group:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${API}/api/groups/${groupId}/messages?user_id=${user.id}&limit=60`
      );

      const payload = response.data || {};
      // Backend now returns { messages, has_more }. Stay tolerant of the old
      // bare-array shape (during rollout / cached responses).
      const latestBatch = Array.isArray(payload) ? payload : (payload.messages || []);
      const moreOlder = Array.isArray(payload) ? false : !!payload.has_more;

      // New-message notification (visual only; group chats stay silent) —
      // detect via the newest message id rather than length, since the list
      // is now a paginated window.
      const latest = latestBatch[latestBatch.length - 1];
      if (lastSeenMsgIdRef.current && latest && latest.id !== lastSeenMsgIdRef.current && latest.sender_id !== user.id) {
        const messagePreview = latest.message_type === 'text'
          ? latest.message_text
          : latest.message_type === 'image'
          ? '📷 Photo'
          : latest.message_type === 'video'
          ? '🎥 Video'
          : latest.message_type === 'voice'
          ? '🎤 Voice message'
          : 'New message';
        showNotification({
          title: `${latest.sender_name || 'Group Member'} - ${group?.name || 'Group Chat'}`,
          body: messagePreview,
          tag: `group-${groupId}`,
          silent: true,
          data: { groupId, senderId: latest.sender_id, messageType: latest.message_type },
        });
      }
      if (latest) lastSeenMsgIdRef.current = latest.id;

      // Merge: preserve any OLDER messages the user already scrolled up to
      // load, then replace the latest window with fresh server data (so
      // edits / status flips / deletions within the window reflect).
      setMessages(prev => {
        if (!prev || prev.length === 0) {
          setHasMore(moreOlder);
          return latestBatch;
        }
        if (latestBatch.length === 0) return prev;
        const oldestNewTs = latestBatch[0].timestamp;
        const newIds = new Set(latestBatch.map(m => m.id));
        const older = prev.filter(m => m.timestamp < oldestNewTs && !newIds.has(m.id));
        // If older messages are already loaded, keep hasMore as-is; otherwise
        // trust the server's has_more for the fresh latest window.
        if (older.length === 0) setHasMore(moreOlder);
        return [...older, ...latestBatch];
      });

      writeInstantCache('group_messages', groupId, latestBatch.slice(-60));
      idbSaveMessages(groupId, latestBatch);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load OLDER messages (scroll-up pagination). Prepends the batch and
  // preserves the visual scroll position so the view doesn't jump.
  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreRef.current) return;
    const current = messagesRef.current;
    if (!current || current.length === 0) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;
    try {
      const before = current[0].timestamp;
      const response = await axios.get(
        `${API}/api/groups/${groupId}/messages?user_id=${user.id}&limit=60&before=${encodeURIComponent(before)}`
      );
      const payload = response.data || {};
      const olderBatch = Array.isArray(payload) ? payload : (payload.messages || []);
      const moreOlder = Array.isArray(payload) ? false : !!payload.has_more;
      if (olderBatch.length > 0) {
        isPrependingRef.current = true;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const dedup = olderBatch.filter(m => !existingIds.has(m.id));
          return [...dedup, ...prev];
        });
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
        });
      }
      setHasMore(moreOlder);
    } catch (error) {
      console.error('Failed to load older messages:', error);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [groupId, user]);
  // Keep the ref pointed at the latest loadOlderMessages for handleScroll.
  loadOlderRef.current = loadOlderMessages;

  const scrollToBottom = (instant = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: instant ? 'instant' : 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // ALWAYS scroll to bottom when messages load - multiple attempts for reliability
  useEffect(() => {
    // Skip when we just PREPENDED older messages (scroll-up pagination) —
    // otherwise the view would yank back to the bottom.
    if (isPrependingRef.current) {
      isPrependingRef.current = false;
      return;
    }
    if (messages.length > 0) {
      // Immediate scroll with instant behavior
      scrollToBottom(true);
      
      // Use requestAnimationFrame for after DOM paint
      requestAnimationFrame(() => {
        scrollToBottom(true);
        setTimeout(() => scrollToBottom(true), 50);
        setTimeout(() => scrollToBottom(true), 150);
        setTimeout(() => scrollToBottom(true), 300);
        setTimeout(() => scrollToBottom(true), 500);
      });
    }
  }, [messages.length, groupId]);

  // Handle image preview
  const handleImageClick = useCallback((imageUrl) => {
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  }, []);

  const closeImagePreview = useCallback(() => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  }, []);

  // Handle input focus - Keyboard fix
  // Only scroll if user is near bottom
  const handleInputFocus = useCallback(() => {
    if (isNearBottomRef.current) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'end'
          });
        }
      }, 100);
    }
  }, []);

  const handleSendMessage = async (e, textFromInput = null) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from form
    let text;
    if (textFromInput) {
      text = textFromInput;
    } else if (e?.target) {
      const formData = new FormData(e.target);
      text = formData.get('message');
    }

    if (!text?.trim()) return;

    try {
      const messageData = {
        sender_id: user.id,
        text: text.trim(),
        image_url: null,
        voice_url: null
      };

      // Add reply reference if replying to a message
      if (replyingTo) {
        messageData.reply_to = {
          message_id: replyingTo.id,
          text: replyingTo.text || '(Media)',
          sender_name: replyingTo.sender_name
        };
      }

      await axios.post(`${API}/api/groups/${groupId}/send-message`, messageData);

      if (e?.target?.reset) {
        e.target.reset();
      }
      setReplyingTo(null); // Clear reply state
      
      // User sent message - force scroll to bottom
      isNearBottomRef.current = true;
      
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Handler for WhatsAppInput send message (for Group Chat)
  const handleWhatsAppSendMessage = (text) => {
    handleSendMessage(null, text);
  };

  // Handler for WhatsAppInput media (for Group Chat)
  const handleGroupMediaSelected = async (media) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
          sender_id: user.id,
          text: media.caption || null,
          image_url: media.type === 'image' || media.type === 'sticker' ? media.url : null,
          voice_url: media.type === 'voice' ? media.url : null,
          is_sticker: media.type === 'sticker',
          client_message_id: media.client_message_id, // dedup on server
          // Point 63 — view-once for admin/handler groups. Backend already
          // supported this (role-gated, per-member tracking); this was the
          // missing piece wiring the picker's toggle through to the send.
          view_once: !!media.view_once,
        });
      
      isNearBottomRef.current = true;
      fetchMessages();
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', 'image');

    try {
      toast.loading('Uploading image...', { id: 'image-upload' });
      
      const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      
      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
          sender_id: user.id,
          text: null,
          image_url: uploadResponse.data.url,
          voice_url: null
        });

      toast.success('Image sent!', { id: 'image-upload' });
      fetchMessages();
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to send image', { id: 'image-upload' });
    }
  };

  const startCamera = async () => {
    try {
      console.log('🎥 Starting camera...');
      
      const constraints = { 
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      };
      
      console.log('📸 Requesting camera with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Camera stream obtained:', stream.getTracks());
      
      // First show the camera modal
      setShowCamera(true);
      
      // Then wait a bit for DOM to render and attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            console.log('📹 Video metadata loaded');
            videoRef.current.play().then(() => {
              console.log('▶️ Video playing');
              toast.success('Camera opened');
            }).catch(err => {
              console.error('❌ Video play failed:', err);
              toast.error('Failed to start video');
            });
          };
        } else {
          console.error('❌ Video ref is still null after showing modal');
          toast.error('Video element not ready');
          // Stop the stream if video element is not ready
          stream.getTracks().forEach(track => track.stop());
          setShowCamera(false);
        }
      }, 100);
    } catch (error) {
      console.error('❌ Camera error:', error);
      const errorMsg = error.name === 'NotAllowedError' 
        ? 'Camera permission denied. Please allow camera access.'
        : error.name === 'NotFoundError'
        ? 'No camera found on device'
        : error.name === 'NotReadableError'
        ? 'Camera is already in use by another app'
        : `Camera error: ${error.message}`;
      toast.error(errorMsg);
      setShowCamera(false);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('file', blob, `photo_${Date.now()}.jpg`);
      formData.append('media_type', 'image');

      try {
        const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        
        await axios.post(`${API}/api/groups/${groupId}/send-message`, {
            sender_id: user.id,
            text: null,
            image_url: uploadResponse.data.url,
            voice_url: null
          });

        stopCamera();
        await fetchMessages();
        toast.success('Photo sent');
      } catch (error) {
        console.error('Failed to send photo:', error);
        toast.error('Failed to send photo');
      }
    }, 'image/jpeg');
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioChunksRef.current = []; // Clear previous data
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          console.log('Audio chunk received:', e.data.size, 'bytes');
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped. Total chunks:', audioChunksRef.current.length);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setShowVoiceRecordModal(true);
      setRecordingDuration(0);
      
      // Start duration timer
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
      console.log('Recording started');
      
    } catch (error) {
      console.error('Microphone access denied:', error);
      toast.error('Please allow microphone access');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    audioChunksRef.current = [];
    setShowVoiceRecordModal(false);
    setRecordingDuration(0);
    toast.info('Voice message cancelled');
  };

  const sendVoiceMessage = async () => {
    console.log('Send clicked. Chunks:', audioChunksRef.current.length);
    
    // Wait a moment for final data
    await new Promise(resolve => setTimeout(resolve, 300));
    
    stopRecording();
    
    // Wait for stop to complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (audioChunksRef.current.length === 0) {
      toast.error('No audio recorded. Please try again.');
      setShowVoiceRecordModal(false);
      return;
    }

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      console.log('Audio blob created:', { 
        size: audioBlob.size, 
        type: audioBlob.type,
        chunks: audioChunksRef.current.length 
      });
      
      if (audioBlob.size === 0) {
        throw new Error('Audio blob is empty');
      }

      const formData = new FormData();
      formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
      formData.append('media_type', 'voice');

      toast.info('Uploading voice message...');

      const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
      
      console.log('Upload success:', uploadResponse.data);

      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
          sender_id: user.id,
          text: null,
          image_url: null,
          voice_url: uploadResponse.data.url
        });

      await fetchMessages();
      toast.success('Voice message sent!');
    } catch (error) {
      console.error('Failed to send voice:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send voice message';
      toast.error(errorMsg);
    }
    
    setShowVoiceRecordModal(false);
    setRecordingDuration(0);
    audioChunksRef.current = [];
  };

  // WhatsApp-like message actions
  const handleMessageLongPress = (message) => {
    setSelectedMessage(message);
    setShowMessageMenu(true);
  };

  const handleCopyMessage = () => {
    if (selectedMessage?.text) {
      navigator.clipboard.writeText(selectedMessage.text);
      toast.success('Message copied to clipboard');
      setShowMessageMenu(false);
      setSelectedMessage(null);
    } else {
      toast.error('Cannot copy media messages');
    }
  };

  const handleReplyMessage = () => {
    if (!selectedMessage) return;
    
    setReplyingTo(selectedMessage);
    setShowMessageMenu(false);
    setSelectedMessage(null);
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleForwardMessage = async () => {
    if (!selectedMessage) return;
    
    try {
      // Fetch user's groups
      const response = await axios.get(`${API}/api/groups/user-groups?user_id=${user.id}`);
      const groups = response.data.filter(g => g.id !== groupId); // Exclude current group
      
      setAvailableGroups(groups);
      setShowForwardModal(true);
      setShowMessageMenu(false);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast.error('Failed to load groups');
    }
  };

  const forwardToGroup = async (targetGroupId) => {
    try {
      await axios.post(`${API}/api/groups/${targetGroupId}/send-message`, {
          sender_id: user.id,
          text: selectedMessage.text || '📷 Forwarded media',
          image_url: selectedMessage.image_url || null,
          voice_url: selectedMessage.voice_url || null
        }, {
          params: {}
        });
      
      toast.success('Message forwarded');
      setShowForwardModal(false);
      setSelectedMessage(null);
      setAvailableGroups([]);
    } catch (error) {
      console.error('Failed to forward message:', error);
      toast.error('Failed to forward message');
    }
  };

  const handleDeleteMessage = async (deleteForEveryone = false) => {
    if (!selectedMessage) return;

    try {
      const endpoint = deleteForEveryone 
        ? `${API}/api/groups/${groupId}/messages/${selectedMessage.id}/delete-for-all?user_id=${user.id}`
        : `${API}/api/groups/${groupId}/messages/${selectedMessage.id}/delete-for-me?user_id=${user.id}`;
      
      await axios.delete(endpoint);

      toast.success(deleteForEveryone ? 'Message deleted for everyone' : 'Message deleted for you');
      fetchMessages();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }

    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  const handleStarMessage = async () => {
    if (!selectedMessage) return;
    
    try {
      // Check if message is already starred by current user
      const isCurrentlyStarred = selectedMessage.starred_by?.includes(user.id);
      const newStarState = !isCurrentlyStarred;
      
      await axios.post(`${API}/api/groups/${groupId}/messages/${selectedMessage.id}/star`, {
        user_id: user.id,
        is_starred: newStarState
      });
      
      toast.success(newStarState ? '⭐ Message starred' : 'Star removed');
      
      // Update local state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === selectedMessage.id
            ? {
                ...msg,
                starred_by: newStarState
                  ? [...(msg.starred_by || []), user.id]
                  : (msg.starred_by || []).filter(id => id !== user.id)
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Star failed:', error);
      toast.error('Failed to star message');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  const onEmojiClick = (emojiObject) => {
    if (inputRef.current) {
      const currentValue = inputRef.current.value || '';
      inputRef.current.value = currentValue + emojiObject.emoji;
    }
    setShowEmojiPicker(false);
  };

  // ==================== NEW TELEGRAM-STYLE FUNCTIONS ====================
  
  // Add reaction to message
  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/messages/${messageId}/reaction`, {
        user_id: user.id,
        emoji: emoji
      });
      
      // Update local state
      setMessages(prevMessages =>
        prevMessages.map(msg => {
          if (msg.id === messageId) {
            const reactions = { ...msg.reactions } || {};
            if (reactions[emoji]) {
              if (reactions[emoji].includes(user.id)) {
                reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
                if (reactions[emoji].length === 0) delete reactions[emoji];
              } else {
                reactions[emoji].push(user.id);
              }
            } else {
              reactions[emoji] = [user.id];
            }
            return { ...msg, reactions };
          }
          return msg;
        })
      );
      
      setShowReactionPicker(false);
      setReactionTargetMessage(null);
      setShowFullEmojiPicker(false);
    } catch (error) {
      console.error('Failed to add reaction:', error);
      toast.error('Failed to add reaction');
    }
  };

  // Handle reaction emoji from full picker
  const onReactionEmojiClick = (emojiObject) => {
    if (reactionTargetMessage) {
      handleAddReaction(reactionTargetMessage.id, emojiObject.emoji);
    }
  };

  // Pin/Unpin message (Admin/Handler only)
  const handlePinMessage = async (action = 'pin', duration = '1d') => {
    if (!selectedMessage) return;

    try {
      await axios.post(`${API}/api/groups/${groupId}/messages/${selectedMessage.id}/pin`, {
        user_id: user.id,
        action: action,
        duration: action === 'pin' ? duration : undefined,
      });

      toast.success(action === 'pin' ? '📌 Message pinned' : 'Pin removed');
      fetchPinnedMessages();
      fetchMessages();
    } catch (error) {
      console.error('Pin failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to pin message');
    }

    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Get view count for message (Admin/Handler only)
  const handleViewMessageViews = async (message) => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/messages/${message.id}/views`);
      setViewersData(response.data);
      setShowViewersModal(true);
    } catch (error) {
      console.error('Failed to get views:', error);
      toast.error('Failed to load view count');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Mute/Unmute group
  const handleMuteGroup = async (muteUntil = null) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/mute`, {
        user_id: user.id,
        is_muted: !isMuted,
        mute_until: muteUntil
      });
      
      setIsMuted(!isMuted);
      toast.success(isMuted ? '🔔 Notifications enabled' : '🔕 Notifications muted');
      setShowMuteOptions(false);
    } catch (error) {
      console.error('Mute failed:', error);
      toast.error('Failed to update mute settings');
    }
  };

  // Copy link from message
  const handleCopyLink = () => {
    if (!selectedMessage?.text) return;
    
    // Extract URLs from text
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = selectedMessage.text.match(urlRegex);
    
    if (urls && urls.length > 0) {
      navigator.clipboard.writeText(urls[0]);
      toast.success('Link copied!');
    } else {
      toast.error('No link found in message');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Download media
  const handleDownloadMedia = async () => {
    if (!selectedMessage) return;
    
    const mediaUrl = selectedMessage.image_url || selectedMessage.voice_url;
    if (!mediaUrl) {
      toast.error('No media to download');
      return;
    }
    
    try {
      let fullUrl;
      if (mediaUrl.startsWith('/api/')) {
        fullUrl = `${API.replace(/\/api\/?$/, '')}${mediaUrl}`;
      } else if (mediaUrl.startsWith('/uploads/')) {
        fullUrl = `${API.replace(/\/api\/?$/, '')}/api${mediaUrl}`;
      } else {
        fullUrl = `${API}${mediaUrl}`;
      }
      
      // Create download link
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = mediaUrl.split('/').pop();
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Check if message has link
  const hasLink = (text) => {
    if (!text) return false;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return urlRegex.test(text);
  };

  // Scroll to pinned message
  const scrollToPinnedMessage = (msgId) => {
    const element = document.getElementById(`msg-${msgId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('bg-yellow-100');
      setTimeout(() => element.classList.remove('bg-yellow-100'), 2000);
    }
  };

  // Navigate pinned messages
  const navigatePinned = (direction) => {
    if (direction === 'next') {
      setCurrentPinnedIndex((prev) => (prev + 1) % pinnedMessages.length);
    } else {
      setCurrentPinnedIndex((prev) => (prev - 1 + pinnedMessages.length) % pinnedMessages.length);
    }
  };

  // Check if user is Admin/Handler
  const isStaff = user?.role === 'super_admin' || user?.role?.includes('handler');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-gray-600">Loading group...</div>
      </div>
    );
  }

  return (
    <>
    <div ref={containerRef} className="fixed inset-0 bg-gray-100 overflow-hidden">
      {/* Header - WhatsApp Style */}
      <div ref={chatHeaderRef} className="fixed top-0 left-0 right-0 bg-white border-b shadow-sm z-50">
        <div className="flex items-center min-h-[56px] px-2">
          {/* Back Button */}
          <button 
            onClick={() => {
              // If we arrived from a specific origin, honor it (e.g., withdraw chat clicked G ▾)
              const fromPath = location.state?.from;
              // Safety: only honor `from` if it looks like a valid route
              // (starts with '/'). Legacy callers used to pass sentinels like
              // 'pw' which navigated to a broken /pw route → white screen.
              if (fromPath && typeof fromPath === 'string' && fromPath.startsWith('/')) {
                navigate(fromPath, { state: location.state?.fromState || {} });
                return;
              }
              // Navigate based on user role
              if (user?.role === 'customer') {
                navigate('/customer/groups');
              } else if (String(group?.system_key || '').startsWith('withdraw_group')) {
                // Withdraw MB groups live inside the handler/admin dashboard's
                // "groups" tab — send the user back there, not to the generic
                // /groups create-first-group page.
                if (user?.role === 'super_admin') {
                  navigate('/admin-dashboard', { state: { activeTab: 'groups' } });
                } else {
                  navigate('/handler-dashboard', { state: { activeTab: 'groups' } });
                }
              } else {
                navigate('/groups');
              }
            }} 
            className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            data-testid="group-chat-back-btn"
          >
            <ArrowLeft size={24} />
          </button>
          
          {/* Group Profile Picture */}
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 ml-1">
            {group?.profile_pic ? (
              <img
                src={`${API}/uploads/group_profiles/${group.profile_pic}`}
                alt="Group"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-gray-600 font-semibold text-lg">
                {group?.name?.charAt(0)?.toUpperCase() || 'G'}
              </span>
            )}
          </div>
          
          {/* Group Name and Member Count — Latest-fix Point 10: tapping the
              name/member block itself opens the group dashboard (WhatsApp
              style). The separate Info icon button has been removed. Kept
              gated to non-customer roles, matching the previous icon. */}
          <div
            className={`flex-1 ml-3 min-w-0 ${user?.role !== 'customer' ? 'cursor-pointer' : ''}`}
            onClick={() => { if (user?.role !== 'customer') navigate(`/groups/${groupId}/dashboard`); }}
            data-testid="group-header-info"
          >
            <h2 className="text-base font-semibold text-gray-900 truncate">{group?.name || 'Group Chat'}</h2>
            <p className="text-xs text-gray-500">
              {group?.members?.length || 0} members
            </p>
            {!isOnline && (
              <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="offline-cache-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Offline — showing saved chats
              </span>
            )}
          </div>

          {/* Withdraw-Group only: three-dot → History + Filter modal (Task A + C) */}
          {user?.role !== 'customer' && String(group?.system_key || '').startsWith('withdraw_group') && (() => {
            // Admin has no restrictions. For handlers, admin controls each
            // sub-option's visibility + scope via `panel_permissions`. Default
            // when a key is absent → visible + scope=both.
            const perms = user?.role === 'super_admin' ? {} : (user?.panel_permissions || {});
            const getP = (k) => {
              const v = perms[k];
              if (v === undefined) return { visible: true, scope: 'both', download: true };
              return { visible: v.visible !== false, scope: v.scope || 'both', download: v.download !== false };
            };
            // Session-37: Summary / Excel / PDF are ALSO gated behind the
            // top-level `reports` handler permission. Admin toggling it off
            // from Handler Management → 🔑 Settings Powers → Reports hides
            // all three at once, regardless of granular panel_permissions.
            const isSuper = user?.role === 'super_admin';
            const reportsAllowed = isSuper || !!(user?.permissions?.reports);
            const historyP = getP('history');
            const summaryPRaw = getP('summary');
            const excelPRaw = getP('excel');
            const pdfPRaw = getP('pdf');
            const summaryP = reportsAllowed ? summaryPRaw : { ...summaryPRaw, visible: false };
            const excelP = reportsAllowed ? excelPRaw : { ...excelPRaw, visible: false };
            const pdfP = reportsAllowed ? pdfPRaw : { ...pdfPRaw, visible: false };
            const showExcelPdf = excelP.visible || pdfP.visible;
            if (!historyP.visible && !summaryP.visible && !showExcelPdf) return null;
            return (
            <div className="relative">
              <button
                onClick={() => setShowGroupMenu(v => !v)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
                data-testid="withdraw-group-menu-btn"
                title="Menu"
              >
                <DotsThreeVertical size={24} weight="bold" />
              </button>
              {showGroupMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg py-2 z-50">
                  {historyP.visible && (
                    <button
                      onClick={() => { setShowGroupMenu(false); setShowWithdrawHistory({ scope: historyP.scope }); }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                      data-testid="withdraw-group-history-btn"
                    >
                      <span className="text-base">📜</span> History &amp; Filter
                    </button>
                  )}
                  {summaryP.visible && (
                    <button
                      onClick={() => { setShowGroupMenu(false); setShowSummary({ scope: summaryP.scope }); }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 border-t"
                      data-testid="withdraw-group-summary-btn"
                    >
                      <span className="text-base">📊</span> Summary
                    </button>
                  )}
                  {showExcelPdf && (
                    <button
                      onClick={() => { setShowGroupMenu(false); setShowReport({ excel: excelP, pdf: pdfP }); }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 border-t"
                      data-testid="withdraw-group-excel-btn"
                    >
                      <span className="text-base">📥</span> Excel / PDF Report
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })()}
          
          {/* Mute Button for Customers */}
          {user?.role === 'customer' && (
            <button
              onClick={() => handleMuteGroup()}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <SpeakerSimpleX size={24} />
              ) : (
                <SpeakerSimpleHigh size={24} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Telegram-Style Pinned Message Bar - Fixed below header */}
      {pinnedMessages.length > 0 && showPinnedBanner && (
        <div ref={pinnedBarRef} className="fixed left-0 right-0 bg-[#e8f5e9] border-l-4 border-[#25D366] px-3 py-2 flex items-center gap-2 z-40 shadow-sm" style={{ top: `${headerHeight}px` }}>
          {/* Pin icon */}
          <PushPin size={18} className="text-[#25D366] flex-shrink-0" weight="fill" />
          
          {/* Pinned message preview - clickable */}
          <div 
            className="flex-1 cursor-pointer min-w-0"
            onClick={() => scrollToPinnedMessage(pinnedMessages[currentPinnedIndex]?.id)}
          >
            <div className="text-xs text-[#128C7E] font-medium truncate">
              {pinnedMessages.length > 1 ? `Pinned #${currentPinnedIndex + 1}` : 'Pinned'}
              <span className="text-gray-600 font-normal ml-1">
                {pinnedMessages[currentPinnedIndex]?.text?.substring(0, 50) || '📷 Media'}
                {(pinnedMessages[currentPinnedIndex]?.text?.length || 0) > 50 ? '...' : ''}
              </span>
            </div>
          </div>
          
          {/* Navigate between pinned messages if multiple */}
          {pinnedMessages.length > 1 && (
            <button 
              onClick={() => setShowAllPinnedMessages(true)}
              className="p-1 text-[#128C7E] hover:bg-[#d4edda] rounded"
              title={`${pinnedMessages.length} pinned`}
            >
              <CaretDown size={16} weight="bold" />
            </button>
          )}
          
          {/* Unpin button - Only for Admin/Handler */}
          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role?.includes('handler')) && (
            <button 
              onClick={async () => {
                // Direct unpin current pinned message
                const msgToUnpin = pinnedMessages[currentPinnedIndex];
                if (!msgToUnpin) return;
                try {
                  await axios.post(`${API}/api/groups/${groupId}/messages/${msgToUnpin.id}/pin`, {
                    user_id: user.id,
                    action: 'unpin'
                  });
                  toast.success('Pin removed');
                  fetchPinnedMessages();
                  fetchMessages();
                } catch (error) {
                  toast.error('Failed to unpin');
                }
              }}
              className="p-1 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded"
              title="Unpin message"
            >
              <X size={16} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* All Pinned Messages Modal */}
      {showAllPinnedMessages && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#e8f5e9]">
              <div className="flex items-center gap-2">
                <PushPin size={20} className="text-[#25D366]" weight="fill" />
                <h3 className="font-semibold text-gray-800">{pinnedMessages.length} Pinned Messages</h3>
              </div>
              <button 
                onClick={() => setShowAllPinnedMessages(false)}
                className="p-2 hover:bg-[#d4edda] rounded-full"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Pinned Messages List */}
            <div className="flex-1 overflow-y-auto">
              {pinnedMessages.map((msg, idx) => (
                <div 
                  key={msg.id}
                  className="px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-[#e8f5e9] transition-colors"
                  onClick={() => {
                    scrollToPinnedMessage(msg.id);
                    setShowAllPinnedMessages(false);
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{msg.sender_name}</span>
                    <span className="text-xs text-[#128C7E]">#{idx + 1}</span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {msg.text || (msg.image_url ? '📷 Photo' : msg.voice_url ? '🎤 Voice' : 'Media')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area - Always starts at header bottom (no extra space) */}
      <div 
        ref={messagesContainerRef}
        className="fixed left-0 right-0 overflow-y-auto p-4 bg-[#E5DDD5]"
        style={{ 
          top: pinnedMessages.length > 0 && showPinnedBanner ? `${headerHeight + pinnedBarHeight}px` : `${headerHeight}px`, 
          bottom: '70px' 
        }}
        onScroll={handleScroll}
      >
        {/* Lazy-load spinner — shown while fetching the previous window of
            older messages after scrolling to the top. */}
        {loadingOlder && (
          <div className="flex justify-center py-3" data-testid="load-older-spinner">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-[#25D366] rounded-full animate-spin" />
          </div>
        )}
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-10">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwnMessage = msg.sender_id === user?.id;
            const hasReactions = msg.reactions && Object.keys(msg.reactions).length > 0;
            
            // Skip if part of image group but not first
            if (isPartOfGroupImageGroup(messages, idx)) return null;
            const imageGroup = getGroupImageGroup(messages, idx);
            
            return (
              <div
                key={msg.id}
                id={`msg-${msg.id}`}
                className={`flex mb-4 ${isOwnMessage ? 'justify-end' : 'justify-start'} transition-colors duration-300`}
              >
                <div className="relative max-w-[75%] md:max-w-md">
                  {/* Pin indicator */}
                  {msg.is_pinned && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <PushPin size={14} className="text-red-500" weight="fill" />
                    </div>
                  )}
                  
                  <div 
                    className={`${
                      msg.is_sticker
                        ? 'bg-transparent shadow-none p-0'
                        : `${
                            msg?.meta?.kind === 'coin_cut_report' && msg?.meta?.status === 'cancelled'
                              ? 'bg-red-100 ring-1 ring-red-300 opacity-90'
                              : msg?.meta?.kind === 'coin_cut_report' && msg?.meta?.status === 'paid'
                                ? 'bg-green-100 ring-1 ring-green-300'
                                : (isOwnMessage ? 'bg-green-50' : 'bg-white')
                          } rounded-lg p-2.5 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${msg.is_pinned ? 'ring-1 ring-red-200' : ''}`
                    }`}
                    onClick={async () => {
                      // Coin-cut entry: click → confirmation popup (paid/cancel decision)
                      if (msg?.meta?.kind === 'coin_cut_report' && msg?.meta?.entry_id && user?.role && ['super_admin','withdrawal_handler','deposit_handler','third_party_withdraw_handler'].includes(user.role)) {
                        setMarkPaidTarget(msg);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Show reaction picker first for customers
                      if (user?.role === 'customer') {
                        setReactionTargetMessage(msg);
                        setShowReactionPicker(true);
                      } else {
                        handleMessageLongPress(msg);
                      }
                    }}
                    onTouchStart={() => {
                      const timer = setTimeout(() => {
                        if (user?.role === 'customer') {
                          setReactionTargetMessage(msg);
                          setShowReactionPicker(true);
                        } else {
                          handleMessageLongPress(msg);
                        }
                      }, 800);
                      setLongPressTimer(timer);
                    }}
                    onTouchEnd={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onMouseDown={() => {
                      const timer = setTimeout(() => {
                        if (user?.role === 'customer') {
                          setReactionTargetMessage(msg);
                          setShowReactionPicker(true);
                        } else {
                          handleMessageLongPress(msg);
                        }
                      }, 800);
                      setLongPressTimer(timer);
                    }}
                    onMouseUp={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onMouseLeave={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                  >
                  {/* Sender Name - Compact style */}
                  {/* For customers: Show "DWS Team" for Admin/Handler messages to keep identities anonymous */}
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-[11px] font-semibold ${isOwnMessage ? 'text-green-700' : 'text-blue-600'}`}>
                      {isOwnMessage ? 'You' : 
                        (user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler')))
                          ? 'DWS Team'
                          : msg.sender_name}
                    </span>
                    {/* Role badge - Hide for customers viewing staff messages */}
                    {msg.sender_role && !(user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler'))) && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        msg.sender_role === 'super_admin' ? 'bg-red-100 text-red-700' :
                        msg.sender_role.includes('handler') ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {msg.sender_role === 'super_admin' ? 'Admin' : 
                         msg.sender_role.includes('handler') ? 'Handler' : 
                         'Customer'}
                      </span>
                    )}
                    {/* DWS Team badge for customers */}
                    {user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler')) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                        Staff
                      </span>
                    )}
                  </div>

                  {/* Reply Reference - WhatsApp Style */}
                  {msg.reply_to && (
                    <div className={`mb-1.5 pl-2 py-1 border-l-3 rounded ${isOwnMessage ? 'border-green-700 bg-green-100/30' : 'border-blue-600 bg-blue-100/30'} text-xs`}>
                      <div className="font-semibold text-[10px] opacity-70">{msg.reply_to.sender_name}</div>
                      <div className="truncate text-[11px] opacity-60">{msg.reply_to.text || '📷 Media'}</div>
                    </div>
                  )}

                  {/* Image Group (merged grid) */}
                  {imageGroup && imageGroup.length > 1 && (
                    <>
                      <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-2 w-[320px] max-w-full">
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => (
                          <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(buildGroupMediaUrl(gMsg.image_url))}>
                            <img src={buildGroupMediaUrl(gMsg.image_url)} alt="" className="w-full h-[120px] object-cover" />
                            {gIdx === 3 && imageGroup.length > 4 && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const lastMsg = imageGroup[imageGroup.length - 1];
                        return lastMsg.text?.trim() ? <p className="text-sm mb-1">{lastMsg.text}</p> : null;
                      })()}
                    </>
                  )}

                  {/* Point 63 — view-once placeholder (image/video). Backend
                      already strips image_url for non-sender viewers, so
                      this and the normal image block below are mutually
                      exclusive in practice. */}
                  {msg.view_once_state && !imageGroup && msg.sender_id !== user.id && msg.view_once_media_type !== 'voice' && (
                    <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/api/groups/${groupId}/messages/${msg.id}/view-once/open`} />
                  )}

                  {/* Single Image or Sticker */}
                  {msg.image_url && !imageGroup && (
                    msg.is_sticker ? (
                      <img
                        src={buildGroupMediaUrl(msg.image_url)}
                        alt="Sticker"
                        className="w-[88px] max-w-[88px] h-auto object-contain block bg-transparent"
                        data-testid="sticker-message"
                      />
                    ) : (
                      <img
                        src={buildGroupMediaUrl(msg.image_url)}
                        alt="Shared"
                        className="rounded-lg mb-2 w-[180px] h-[180px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => handleImageClick(buildGroupMediaUrl(msg.image_url))}
                        onLoad={() => console.log('✅ Image loaded:', msg.image_url)}
                        onError={(e) => {
                          console.error('❌ Image load error:', msg.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    )
                  )}

                  {/* Point 63 — view-once placeholder (voice). */}
                  {msg.view_once_state && msg.sender_id !== user.id && msg.view_once_media_type === 'voice' && (
                    <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/api/groups/${groupId}/messages/${msg.id}/view-once/open`} />
                  )}

                  {/* Voice */}
                  {msg.voice_url && (
                    <VoiceMessage
                      audioUrl={(() => {
                        // Smart URL handling for voice files
                        if (msg.voice_url.startsWith('/api/')) {
                          return `${API.replace(/\/api\/?$/, '')}${msg.voice_url}`;
                        } else if (msg.voice_url.startsWith('/uploads/')) {
                          return `${API.replace(/\/api\/?$/, '')}/api${msg.voice_url}`;
                        } else {
                          return `${API}${msg.voice_url}`;
                        }
                      })()} 
                    />
                  )}

                  {/* Text - WhatsApp Style with Clickable Links */}
                  {msg.text && (
                    <p
                      className={`break-words text-[13px] leading-[1.4] whitespace-pre-line ${
                        msg?.meta?.kind === 'coin_cut_report' && msg?.meta?.status === 'paid'
                          ? 'text-green-800'
                          : 'text-gray-800'
                      }`}
                    >
                      {msg?.meta?.kind === 'coin_cut_report' && (
                        <span className={`inline-block mb-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          msg?.meta?.status === 'paid'
                            ? 'bg-green-600 text-white'
                            : msg?.meta?.status === 'cancelled'
                              ? 'bg-red-600 text-white'
                              : 'bg-amber-500 text-white'
                        }`}>
                          {msg?.meta?.status === 'paid'
                            ? `✓ Paid${msg?.meta?.paid_by ? ` · ${msg.meta.paid_by}` : ''}${msg?.meta?.utr_number ? ` · UTR ${msg.meta.utr_number}` : ''}`
                            : msg?.meta?.status === 'cancelled'
                              ? `✖ Cancelled${msg?.meta?.cancelled_by ? ` · ${msg.meta.cancelled_by}` : ''}`
                              : 'Pending'}
                        </span>
                      )}
                      {/* Session-32 (Feb-13-2026): Removed the "✕ Remove"
                          button that used to appear on cancelled coin_cut
                          messages. Per user spec, once a cancel notice lands
                          in the group it must stay there PERMANENTLY — no
                          admin/handler can hide it. The withdraw entry is
                          still marked cancelled in Summary/reports, but the
                          group audit trail is now immutable. */}
                      {msg?.meta?.kind === 'coin_cut_report' && msg?.meta?.edited && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditEntry({ type: 'withdraw', entryId: msg.meta.entry_id }); }}
                          className="ml-2 inline-flex items-center text-[10px] text-slate-500 hover:text-slate-800 italic"
                          data-testid={`gm-edited-badge-${msg.id}`}
                          title="View / edit history"
                        >edited · {msg?.meta?.edit_count || '?'}/2</button>
                      )}
                      {msg?.meta?.kind === 'coin_cut_report' && <br />}
                      {msg.text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
                        if (part.match(/^https?:\/\//)) {
                          return (
                            <a
                              key={index}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {part}
                            </a>
                          );
                        }
                        return part;
                      })}
                    </p>
                  )}

                  {/* Timestamp - WhatsApp Style with Star Icon */}
                  <div className="flex items-center justify-end gap-1 mt-1">
                    {msg.starred_by?.includes(user?.id) && (
                      <span className="text-yellow-500 text-[11px]">⭐</span>
                    )}
                    {/* View count for Admin/Handler */}
                    {isStaff && msg.viewed_by && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <Eye size={10} />
                        {msg.viewed_by.length}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {new Date(msg.timestamp).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  </div>
                  
                  {/* Reactions Display - Below message bubble */}
                  {hasReactions && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      {Object.entries(msg.reactions).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                            users.includes(user?.id) 
                              ? 'bg-blue-100 border border-blue-300' 
                              : 'bg-gray-100 border border-gray-200'
                          } hover:bg-blue-50`}
                        >
                          <span>{emoji}</span>
                          <span className="text-gray-600">{users.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to Bottom Button */}
      {showScrollToBottom && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-24 right-4 z-40 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-colors border"
        >
          <CaretDown size={24} className="text-gray-600" weight="bold" />
        </button>
      )}

      {/* Reaction Picker Modal (Customer Long Press) */}
      {showReactionPicker && reactionTargetMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center"
          onClick={() => {
            setShowReactionPicker(false);
            setReactionTargetMessage(null);
            setShowFullEmojiPicker(false);
          }}
        >
          <div className="bg-white rounded-2xl p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Quick Reactions */}
            <div className="flex gap-2 mb-3">
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleAddReaction(reactionTargetMessage.id, emoji)}
                  className="text-2xl hover:scale-125 transition-transform p-2"
                >
                  {emoji}
                </button>
              ))}
              <button
                onClick={() => setShowFullEmojiPicker(true)}
                className="text-2xl hover:scale-110 transition-transform p-2 text-gray-400"
              >
                ➕
              </button>
            </div>
            
            {/* Full Emoji Picker */}
            {showFullEmojiPicker && (
              <div className="mt-2">
                <EmojiPicker onEmojiClick={onReactionEmojiClick} height={300} width={280} />
              </div>
            )}
            
            {/* Additional Options for Customer */}
            <div className="border-t pt-3 space-y-2">
              {/* Copy Text */}
              {reactionTargetMessage.text && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(reactionTargetMessage.text);
                    toast.success('Copied!');
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Copy size={18} className="text-gray-600" />
                  <span className="text-sm">Copy Text</span>
                </button>
              )}
              
              {/* Copy Link */}
              {hasLink(reactionTargetMessage.text) && (
                <button
                  onClick={() => {
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    const urls = reactionTargetMessage.text.match(urlRegex);
                    if (urls) {
                      navigator.clipboard.writeText(urls[0]);
                      toast.success('Link copied!');
                    }
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Link size={18} className="text-gray-600" />
                  <span className="text-sm">Copy Link</span>
                </button>
              )}
              
              {/* Download Media */}
              {(reactionTargetMessage.image_url || reactionTargetMessage.voice_url) && (
                <button
                  onClick={() => {
                    const mediaUrl = reactionTargetMessage.image_url || reactionTargetMessage.voice_url;
                    let fullUrl;
                    if (mediaUrl.startsWith('/api/')) {
                      fullUrl = `${API.replace(/\/api\/?$/, '')}${mediaUrl}`;
                    } else if (mediaUrl.startsWith('/uploads/')) {
                      fullUrl = `${API.replace(/\/api\/?$/, '')}/api${mediaUrl}`;
                    } else {
                      fullUrl = `${API}${mediaUrl}`;
                    }
                    const link = document.createElement('a');
                    link.href = fullUrl;
                    link.download = mediaUrl.split('/').pop();
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Download started');
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <DownloadSimple size={18} className="text-gray-600" />
                  <span className="text-sm">Download Media</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Options Menu (Admin/Handler - WhatsApp-like) */}
      {showMessageMenu && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={() => setShowMessageMenu(false)}>
          <div className="bg-white rounded-lg p-4 w-80 max-w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Quick Reactions Row */}
            <div className="flex justify-center gap-2 mb-4 pb-3 border-b">
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleAddReaction(selectedMessage.id, emoji)}
                  className="text-2xl hover:scale-125 transition-transform p-1"
                >
                  {emoji}
                </button>
              ))}
              <button
                onClick={() => {
                  setReactionTargetMessage(selectedMessage);
                  setShowMessageMenu(false);
                  setShowReactionPicker(true);
                  setShowFullEmojiPicker(true);
                }}
                className="text-xl hover:scale-110 transition-transform p-1 text-gray-400"
              >
                ➕
              </button>
            </div>
            
            <div className="mb-3 p-3 bg-gray-50 rounded border-l-4 border-green-600">
              <p className="text-sm text-gray-700 line-clamp-2">
                {selectedMessage.text || '(Media message)'}
              </p>
            </div>

            <div className="space-y-1">
              {/* Copy */}
              {selectedMessage.text && (
                <button
                  onClick={handleCopyMessage}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Copy size={20} className="text-gray-600" />
                  <span className="font-medium">Copy Message</span>
                </button>
              )}

              {/* Copy Link */}
              {hasLink(selectedMessage.text) && (
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Link size={20} className="text-gray-600" />
                  <span className="font-medium">Copy Link</span>
                </button>
              )}

              {/* Download Media */}
              {(selectedMessage.image_url || selectedMessage.voice_url) && (
                <button
                  onClick={handleDownloadMedia}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <DownloadSimple size={20} className="text-gray-600" />
                  <span className="font-medium">Download Media</span>
                </button>
              )}

              {/* Reply */}
              <button
                onClick={handleReplyMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowBendUpLeft size={20} className="text-gray-600" />
                <span className="font-medium">Reply</span>
              </button>

              {/* Forward */}
              <button
                onClick={handleForwardMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ShareFat size={20} className="text-gray-600" />
                <span className="font-medium">Forward</span>
              </button>

              {/* Pin/Unpin (Admin/Handler only) */}
              {isStaff && (
                <button
                  onClick={() => {
                    if (selectedMessage.is_pinned) {
                      handlePinMessage('unpin');
                    } else if (pinnedMessages.length >= 2) {
                      toast.error('Maximum 2 pinned messages — unpin one first');
                      setShowMessageMenu(false);
                    } else {
                      setShowMessageMenu(false);
                      setShowPinDurationPicker(true);
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {selectedMessage.is_pinned ? (
                    <>
                      <PushPinSlash size={20} className="text-gray-600" />
                      <span className="font-medium">Unpin Message</span>
                    </>
                  ) : (
                    <>
                      <PushPin size={20} className="text-gray-600" />
                      <span className="font-medium">Pin Message</span>
                    </>
                  )}
                </button>
              )}

              {/* View Count (Admin/Handler only) */}
              {isStaff && (
                <button
                  onClick={() => handleViewMessageViews(selectedMessage)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Eye size={20} className="text-gray-600" />
                  <span className="font-medium">View Message Views</span>
                </button>
              )}

              {/* Star */}
              <button
                onClick={handleStarMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Star size={20} className="text-gray-600" />
                <span className="font-medium">{selectedMessage.starred_by?.includes(user?.id) ? 'Unstar' : 'Star'}</span>
              </button>

              {/* Edit — point 18: groups are the one chat type that keeps
                  message editing. Own text messages only; the backend also
                  blocks automation notices and the Withdraw Clear Section. */}
              {selectedMessage.sender_id === user?.id && selectedMessage.text && (
                <button
                  onClick={() => {
                    setEditingGroupMsg(selectedMessage);
                    setEditGroupText(selectedMessage.text || '');
                    setShowMessageMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="edit-group-message-btn"
                >
                  <PencilSimple size={20} className="text-gray-600" />
                  <span className="font-medium">Edit</span>
                </button>
              )}

              {/* Session-32 (Feb-13-2026): Removed "Delete for Me" and
                  "Delete for Everyone" from the group long-press menu per
                  user spec — group messages are now permanent audit-trail,
                  no one can delete them from the group view. Reply / Forward
                  / Pin / Star / Copy / View Views / reactions all remain. */}

              {/* Cancel */}
              <button
                onClick={() => setShowMessageMenu(false)}
                className="w-full px-4 py-3 mt-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Message Modal */}
      {editingGroupMsg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-5 shadow-2xl" data-testid="edit-group-message-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Edit Message</h3>
            <textarea
              value={editGroupText}
              onChange={(e) => setEditGroupText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm min-h-[80px] resize-none"
              autoFocus
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setEditingGroupMsg(null); setEditGroupText(''); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!editGroupText.trim()) { toast.error('Message cannot be empty'); return; }
                  try {
                    await axios.put(`${API}/api/groups/${groupId}/messages/${editingGroupMsg.id}/edit`, {
                      user_id: user.id,
                      text: editGroupText.trim(),
                    });
                    setMessages(prev => prev.map(m => m.id === editingGroupMsg.id ? { ...m, text: editGroupText.trim(), edited: true } : m));
                    toast.success('Message edited');
                    setEditingGroupMsg(null);
                    setEditGroupText('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to edit');
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showPinDurationPicker && (
        <PinDurationPicker
          onClose={() => { setShowPinDurationPicker(false); setSelectedMessage(null); }}
          onPick={async (duration) => {
            setShowPinDurationPicker(false);
            await handlePinMessage('pin', duration);
          }}
        />
      )}

      {/* Voice Recording Modal - WhatsApp Style */}
      {showVoiceRecordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
            <div className="text-center mb-6">
              <div className="relative inline-block">
                <Microphone size={64} className="text-red-600 mx-auto mb-3 animate-pulse" weight="fill" />
                {isRecording && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-600 rounded-full animate-ping"></div>
                )}
              </div>
              <p className="text-3xl font-bold text-gray-800 mb-1">
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </p>
              <p className="text-gray-500">Recording voice message...</p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={cancelRecording} 
                className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
              >
                <X size={20} weight="bold" />
                Cancel
              </button>
              <button 
                onClick={sendVoiceMessage} 
                className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <PaperPlaneRight size={20} weight="bold" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black z-[60] flex flex-col">
          <div className="flex-1 relative overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          <div className="bg-black bg-opacity-90 p-6 flex items-center justify-around">
            <button
              onClick={stopCamera}
              className="text-white bg-red-600 px-8 py-4 rounded-full font-semibold hover:bg-red-700 transition-colors text-lg"
            >
              Cancel
            </button>
            <button
              onClick={capturePhoto}
              className="bg-white w-20 h-20 rounded-full flex items-center justify-center border-4 border-white hover:opacity-80 transition-opacity"
            >
              <div className="w-16 h-16 bg-white rounded-full"></div>
            </button>
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Forward to...</h3>
              <button
                onClick={() => {
                  setShowForwardModal(false);
                  setAvailableGroups([]);
                  setSelectedMessage(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Groups List */}
            <div className="flex-1 overflow-y-auto p-4">
              {availableGroups.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No other groups available
                </div>
              ) : (
                <div className="space-y-2">
                  {availableGroups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => forwardToGroup(group.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      {group.profile_pic ? (
                        <img
                          src={`${API}/uploads/group_profiles/${group.profile_pic}`}
                          alt={group.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Users size={24} className="text-green-600" weight="bold" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{group.name}</div>
                        <div className="text-sm text-gray-500">
                          {group.members?.length || 0} members
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Count Modal */}
      {showViewersModal && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setShowViewersModal(false)}>
          <div className="bg-white rounded-lg max-w-sm w-full max-h-[60vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center bg-green-600 text-white">
              <div className="flex items-center gap-2">
                <Eye size={24} weight="bold" />
                <h3 className="text-lg font-semibold">Message Views</h3>
              </div>
              <button onClick={() => setShowViewersModal(false)} className="hover:bg-green-700 p-1 rounded">
                <X size={24} weight="bold" />
              </button>
            </div>
            
            <div className="p-4 text-center border-b">
              <div className="text-3xl font-bold text-green-600">{viewersData.view_count}</div>
              <div className="text-sm text-gray-500">Total Views</div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {viewersData.viewers?.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  No views yet
                </div>
              ) : (
                <div className="space-y-3">
                  {viewersData.viewers?.map((viewer, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-800">{viewer.name}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(viewer.viewed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Input Area - WhatsApp Style */}
      <div ref={inputContainerRef} className="flex-shrink-0">
        {/* Read-Only Banner for Customers in Group Chats */}
        {user?.role === 'customer' ? (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-50">
            <div className="bg-gray-100 border border-gray-200 text-gray-600 py-3 px-4 flex items-center justify-center gap-2 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">This is a broadcast channel - You can only view messages</span>
            </div>
          </div>
        ) : (
          <div className="fixed bottom-0 left-0 right-0 z-50">
            <WhatsAppInput
              onSendMessage={handleWhatsAppSendMessage}
              onSendMedia={handleGroupMediaSelected}
              onOpenMediaPicker={() => setShowMediaPicker(true)}
              placeholder="Type a message..."
              disabled={false}
              replyingTo={replyingTo}
              onCancelReply={cancelReply}
              userId={user?.id}
              userRole={user?.role || 'customer'}
            />
          </div>
        )}
      </div>

    </div>

    {/* Media Picker Modal for Group Chat */}
    {showMediaPicker && (
      <MediaPicker
        onMediaSelected={handleGroupMediaSelected}
        onClose={() => setShowMediaPicker(false)}
      />
    )}

    {/* Image Preview Modal - Outside main container */}
    {showImagePreview && previewImageUrl && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeImagePreview();
          }
        }}
      >
        <button 
          onClick={closeImagePreview}
          className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300 z-60 w-10 h-10 flex items-center justify-center bg-black bg-opacity-50 rounded-full"
        >
          ×
        </button>
        <img 
          src={previewImageUrl} 
          alt="Preview" 
          className="max-w-full max-h-full object-contain cursor-zoom-out"
          onClick={closeImagePreview}
        />
      </div>
    )}

    {/* Withdraw Group — History / Paid-Pending Modal */}
    <WithdrawHistoryModal
      open={showWithdrawHistory}
      onClose={() => setShowWithdrawHistory(false)}
      actorId={user?.id}
      actorRole={user?.role}
      onJumpToMessage={(msgId) => {
        setShowWithdrawHistory(false);
        // Give React a beat to unmount modal, then scroll to the pinned message
        setTimeout(() => scrollToPinnedMessage(msgId), 300);
      }}
    />
    <SummaryModal open={showSummary} onClose={() => setShowSummary(false)} userId={user?.id} assignedMbs={user?.assigned_mbs} isAdmin={user?.role === 'super_admin'} />
    <WithdrawReportModal
      open={!!showReport}
      onClose={() => setShowReport(false)}
      actorRole={user?.role}
      excelPerm={typeof showReport === 'object' ? showReport?.excel : undefined}
      pdfPerm={typeof showReport === 'object' ? showReport?.pdf : undefined}
    />

    {/* Mark as Paid + Cancel Request confirmation popup (coin_cut click — Feature 4 + 7) */}
    {markPaidTarget && (
      <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setMarkPaidTarget(null); }}>
        <MarkPaidCancelPopup
          msg={markPaidTarget}
          user={user}
          onDone={() => setMarkPaidTarget(null)}
          onOpenEdit={(entryId) => { setMarkPaidTarget(null); setEditEntry({ type: 'withdraw', entryId }); }}
          onLocalUpdate={(patch) => setMessages(prev => prev.map(m => m.id === markPaidTarget.id ? { ...m, meta: { ...(m.meta||{}), ...patch } } : m))}
        />
      </div>
    )}

    {/* Edit deposit/withdraw entry modal */}
    {editEntry && (
      <EditEntryModal
        open={!!editEntry}
        type={editEntry.type}
        entryId={editEntry.entryId}
        actorId={user.id}
        onClose={() => setEditEntry(null)}
        showPaidFields
        onEdited={() => {}}
      />
    )}
    </>
  );
};

// --- Feature 4 + 7 popup ---
function MarkPaidCancelPopup({ msg, user, onDone, onLocalUpdate, onOpenEdit }) {
  const [mode, setMode] = React.useState('choose'); // choose | paid | cancel
  const [utr, setUtr] = React.useState('');
  const [utrDup, setUtrDup] = React.useState({ exists: false, matches: [] });
  const utrDupTimer = React.useRef(null);
  const [reference, setReference] = React.useState('');
  const [bankAccount, setBankAccount] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [utrBlockMode, setUtrBlockMode] = React.useState('warn');
  const [showTrail, setShowTrail] = React.useState(false);
  const isPaid = msg?.meta?.status === 'paid';
  const isCancelled = msg?.meta?.status === 'cancelled';
  const isAdmin = user?.role === 'super_admin';

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/settings`).then(r => {
      if (!cancelled) setUtrBlockMode(r.data?.utr_block_mode === 'block' ? 'block' : 'warn');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const doPaid = async () => {
    if (!isPaid) {
      if (!utr.trim()) { toast.error('UTR number is required'); return; }
      if (!bankAccount.trim()) { toast.error('Paid-from bank / UPI is required'); return; }
    } else if (!isAdmin) {
      // Point 20 — handler has no revert capability at all once paid, only
      // the pre-paid cancel-with-reason. The button is hidden for handlers
      // in this state (see render below); this is just a defensive guard.
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/withdraw-entries/${msg.meta.entry_id}/mark-paid`, {
          actor_id: user.id,
          status: isPaid ? 'pending' : 'paid',
          utr_number: utr.trim(),
          paid_from_bank: bankAccount.trim(),
          reference: reference.trim() || null,
          bank_account: bankAccount.trim() || null,
        });
      // Server deduped a double-tap/retry — nothing was written on THIS call.
      // Never fake a local success; just refetch the real state.
      if (res.data?.duplicate_ignored) {
        toast.info('Already processed — refreshing');
        onDone();
        return;
      }
      onLocalUpdate({
        status: isPaid ? 'pending' : 'paid',
        utr_number: isPaid ? null : utr.trim(),
        paid_reference: isPaid ? null : (reference.trim() || null),
        paid_bank_account: isPaid ? null : (bankAccount.trim() || null),
        paid_from_bank: isPaid ? null : (bankAccount.trim() || null),
        paid_by: isPaid ? null : user.username,
      });
      toast.success(isPaid ? 'Reverted to Pending' : `✓ Marked as Paid (UTR: ${utr.trim()})`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!reason.trim()) { toast.error('Cancel reason is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/withdraw-entries/${msg.meta.entry_id}/cancel`, {
        actor_id: user.id, reason: reason.trim(),
      });
      onLocalUpdate({ status: 'cancelled', cancel_reason: reason.trim(), cancelled_by: user.username });
      toast.success('Request cancelled');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const formNav = useArrowKeyFormNav(() => {
    if (busy) return;
    if (mode === 'cancel') doCancel(); else doPaid();
  });

  return (
    <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl overflow-hidden" data-testid="mark-paid-popup" {...formNav}>
      <div className="px-4 py-3 text-white font-bold text-sm" style={{
        background: mode === 'cancel' ? '#ef4444' : (isPaid ? '#f59e0b' : '#10b981')
      }}>
        {mode === 'cancel' ? 'Cancel Request' : (isPaid ? 'Revert to Pending?' : 'Mark as Paid?')}
      </div>
      <div className="p-4">
        <div className="text-xs text-gray-600 mb-3 whitespace-pre-line max-h-32 overflow-y-auto">{msg?.text}</div>

        {mode === 'choose' && !isCancelled && (
          <>
            {!isPaid && (
              <>
                <label className="text-xs text-gray-600 mb-1 block font-semibold">UTR Number <span className="text-red-500">*</span></label>
                <input autoFocus value={utr} onChange={(e) => {
                    const v = e.target.value.trim().toUpperCase();
                    setUtr(v);
                    if (utrDupTimer.current) clearTimeout(utrDupTimer.current);
                    if (v.length >= 6) {
                      utrDupTimer.current = setTimeout(async () => {
                        try {
                          const { data } = await axios.get(`${API}/api/utr/check`, { params: { utr: v } });
                          // Ignore matches that point to the CURRENT entry being edited (revert-then-repaid case).
                          const currentId = msg?.meta?.entry_id;
                          const filtered = (data.matches || []).filter(m => m.entry_id !== currentId);
                          setUtrDup({ exists: filtered.length > 0, matches: filtered });
                        } catch { setUtrDup({ exists: false, matches: [] }); }
                      }, 400);
                    } else {
                      setUtrDup({ exists: false, matches: [] });
                    }
                  }}
                  placeholder="Enter UTR number"
                  className={`w-full px-3 py-2 border rounded text-sm mb-1 ${utrDup.exists ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                  data-testid="mark-paid-utr-input" />
                {utrDup.exists && (
                  <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 font-semibold" data-testid="mark-paid-utr-duplicate">
                    ⚠️ UTR already used on {utrDup.matches.length} other entry(s):{' '}
                    {utrDup.matches.slice(0, 2).map(m => `${m.type}·${m.customer_username || '-'}·₹${m.amount}`).join(' · ')}
                  </div>
                )}
                <label className="text-xs text-gray-600 mb-1 block font-semibold">Paid From (bank / UPI) <span className="text-red-500">*</span></label>
                <input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)}
                  placeholder="e.g. HDFC ****1234 or our-upi@paytm"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-2"
                  data-testid="mark-paid-bank-input" />
                <label className="text-xs text-gray-600 mb-1 block font-semibold">Reference <span className="text-gray-400">(optional)</span></label>
                <input value={reference} onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g. Ref#12345 or note"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3"
                  data-testid="mark-paid-reference-input" />
              </>
            )}
            {/* Point 20 — once paid, only admin gets a Revert action; handler
                gets nothing here (their only lever is the pre-paid cancel
                button below, which is itself hidden once paid). */}
            {(!isPaid || isAdmin) ? (
              <button onClick={doPaid} disabled={busy || (!isPaid && utrBlockMode === 'block' && utrDup.exists)} data-testid="mark-paid-confirm-btn"
                className="w-full py-3 rounded-lg text-white font-bold shadow disabled:opacity-50 mb-2"
                style={{ background: isPaid ? '#f59e0b' : '#10b981' }}>
                {isPaid ? 'Revert to Pending' : (utrBlockMode === 'block' && utrDup.exists ? '🚫 Duplicate UTR' : '✓ Mark as Paid')}
              </button>
            ) : (
              <div className="text-xs text-gray-500 text-center py-2 mb-2">Already paid. Ask admin to revert if this needs to change.</div>
            )}
            {/* Edit + Cancel are ONLY meaningful for pending entries. Once
                paid, admin/handler must first revert to pending — enforced by
                hiding these buttons entirely (user's spec, Feb 2026).
                
                Feb-12-2026 (session 32): Removed the "Edit / Request Edit"
                button from GROUP chats entirely — edit stays available only in
                the customer chat context. Also removed the handler-specific
                "cancel via revert-request" pipeline: every handler + admin now
                fires the direct /cancel endpoint (backend already permits
                withdrawal_handler / deposit_handler / third_party). Backend
                takes care of the customer auto-notice ("❌ आपका last withdraw
                cancel हो गया है") and stamps meta.cancelled_by + cancel_reason
                on the group message. */}
            {!isPaid && (
              <button
                onClick={() => setMode('cancel')}
                className="w-full py-2 rounded-lg text-red-600 border border-red-300 text-xs font-semibold hover:bg-red-50"
                data-testid="cancel-request-open-btn"
              >
                ✖ Cancel this request
              </button>
            )}
          </>
        )}

        {mode === 'cancel' && (
          <>
            <label className="text-xs text-gray-600 mb-1 block font-semibold">Cancel Reason *</label>
            <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} placeholder="Why is this being cancelled?"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3"
              data-testid="cancel-reason-input" />
            <button onClick={doCancel} disabled={busy} data-testid="cancel-confirm-btn"
              className="w-full py-3 rounded-lg text-white font-bold bg-red-600 disabled:opacity-50 mb-2">
              ✖ Confirm Cancel
            </button>
            <button onClick={() => setMode('choose')}
              className="w-full py-1.5 text-xs text-gray-500 hover:underline">← Back</button>
          </>
        )}

        {isCancelled && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
            <div className="font-bold">✖ CANCELLED</div>
            <div>By: {msg?.meta?.cancelled_by || '-'}</div>
            <div>Reason: {msg?.meta?.cancel_reason || '-'}</div>
          </div>
        )}
      </div>
      <UtrTrailModal
        utr={utr.trim()}
        open={showTrail}
        onClose={() => setShowTrail(false)}
        actorRole={user?.role}
        initialMatches={utrDup.matches}
      />
    </div>
  );
}

export default GroupChatPage;
